import React from 'react';

import Box from '@mui/material/Box';
import { useTheme } from '@mui/material/styles';
import type { Theme } from '@mui/material/styles';

import BillGlyph from 'assets/icons/calendar-schedule-clock.inline.svg';
import IncomeGlyph from 'assets/icons/open-envelope-currency-dollar.inline.svg';
import RefundGlyph from 'assets/icons/refundGlyph.inline.svg';

export type SeriesIconKind = 'bill' | 'income' | 'refund';
export type SeriesIconSize = 'sm' | 'md' | 'lg' | 'xl';
export type SeriesIconShape = 'round' | 'square';

/** Shared refund/income/bill precedence rule backing every avatar `kind` derivation. */
export const getSeriesIconKind = (isRefund: boolean, isIncome: boolean): SeriesIconKind => {
  if (isRefund) {
    return 'refund';
  }
  return isIncome ? 'income' : 'bill';
};

export type SeriesIconProps = {
  kind: SeriesIconKind;
  size?: SeriesIconSize;
  shape?: SeriesIconShape;
};

/**
 * Figma's "Bill icon"/"Income icon"/"Refund icon" components all share this
 * four square-size scale (Getting started flow — Bill Setup, node
 * 2129:47228): Small/Medium/Large/XL square. Box and glyph dimensions don't
 * reduce to a single shared padding formula across sizes, so each is listed
 * explicitly rather than derived.
 */
const SIZE_CONFIG: Record<SeriesIconSize, { box: number; glyph: number; radius: 'md' | 'lg' | 'xl' | '2xl' }> = {
  sm: { box: 24, glyph: 18, radius: 'md' }, // 6px radius
  md: { box: 36, glyph: 22, radius: 'lg' }, // 8px radius
  lg: { box: 44, glyph: 28, radius: 'xl' }, // 12px radius
  xl: { box: 56, glyph: 35, radius: '2xl' }, // 16px radius
};

const GLYPHS: Record<SeriesIconKind, React.FunctionComponent<React.SVGAttributes<SVGElement>>> = {
  bill: BillGlyph,
  income: IncomeGlyph,
  refund: RefundGlyph,
};

/**
 * The glyph color for `bill`/`income` comes from Figma's `legend/bill/dark`
 * and `legend/income/dark` variables, which `theme.palette.legend.billDark`/
 * `incomeDark` already resolve per mode — a different shade than
 * `theme.palette.legend.bill`/`income`, which is used for other legend UI,
 * not these glyphs. `refund` has no `legend/refund/dark` variable in Figma at
 * all; its glyph color resolves from `info/dark` instead, which is
 * `theme.palette.info.dark` (confirmed via the node's variable defs).
 */
function getGlyphColor(theme: Theme, kind: SeriesIconKind): string {
  switch (kind) {
    case 'bill':
      return theme.palette.legend.billDark;
    case 'income':
      return theme.palette.legend.incomeDark;
    case 'refund':
      return theme.palette.info.dark;
  }
}

/**
 * A square or circular gradient tile around a BillScout series-type glyph —
 * shared shape for the "Bill icon"/"Income icon"/"Refund icon" Figma
 * components, which are identical apart from glyph and color. Centered via
 * flex rather than padding, since the per-size box/glyph dimensions don't
 * reduce to one padding formula across sizes.
 */
export default function SeriesIcon({ kind, size = 'lg', shape = 'square' }: SeriesIconProps): React.JSX.Element {
  const theme = useTheme();
  const { box, glyph, radius } = SIZE_CONFIG[size];
  const Glyph = GLYPHS[kind];
  const tint = theme.palette.seriesIconTint[kind];

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
        width: box,
        height: box,
        overflow: 'hidden',
        borderRadius: shape === 'round' ? '50%' : radius,
        border: `1px solid ${tint}`,
        background: `linear-gradient(0deg, ${tint} 0%, ${theme.palette.background.paper} 100%)`,
        '& svg': {
          display: 'block',
          overflow: 'visible',
        },
      }}
    >
      <Glyph width={glyph} height={glyph} viewBox='0 0 28 28' color={getGlyphColor(theme, kind)} />
    </Box>
  );
}
